import { useState } from "react";
import { User, Save, LogOut, ShieldCheck, Info } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAppMode, MODES } from "@/context/AppModeContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Profile() {
  const { user, logout, patchUser } = useAuth();
  const { mode, setMode } = useAppMode();
  const [name, setName] = useState(user?.name || "");
  const [country, setCountry] = useState(user?.country || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/profile", { name, country });
      patchUser(data);
      toast.success("Profile saved");
    } catch { toast.error("Could not save."); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <h1 className="font-head font-extrabold text-3xl sm:text-4xl flex items-center gap-3"><User className="w-8 h-8 text-primary" />Profile</h1>

      <div className="rounded-lg border border-border bg-card p-5 sm:p-6 space-y-4">
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name" className="w-full rounded-md border border-input bg-background px-3 py-2.5 outline-none focus:ring-2 focus:ring-ring" />
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Email</label>
          <input value={user?.email || ""} disabled className="w-full rounded-md border border-input bg-secondary px-3 py-2.5 text-muted-foreground" />
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Country (optional — helps localise examples)</label>
          <input value={country} onChange={(e) => setCountry(e.target.value)} data-testid="profile-country" placeholder="e.g. India, UK, USA" className="w-full rounded-md border border-input bg-background px-3 py-2.5 outline-none focus:ring-2 focus:ring-ring" />
        </div>
        <button onClick={save} disabled={saving} data-testid="profile-save" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground font-bold px-5 py-2.5 hover:brightness-110 disabled:opacity-50 transition"><Save className="w-4 h-4" />Save</button>
      </div>

      <div className="rounded-lg border border-border bg-card p-5 sm:p-6 space-y-3">
        <h2 className="font-head font-bold text-lg">Experience mode</h2>
        <p className="text-sm text-muted-foreground">Changes the whole interface, language and difficulty. Override anytime.</p>
        <Select value={mode} onValueChange={setMode}>
          <SelectTrigger data-testid="profile-mode" className="w-full sm:w-72"><SelectValue /></SelectTrigger>
          <SelectContent>{MODES.map((m) => <SelectItem key={m.key} value={m.key}>{m.label} mode</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {user?.goals?.length > 0 && (
        <div className="rounded-lg border border-border bg-card p-5 sm:p-6">
          <h2 className="font-head font-bold text-lg mb-3">Your focus areas</h2>
          <div className="flex flex-wrap gap-2">{user.goals.map((g) => <span key={g} className="rounded-full border border-border px-3 py-1.5 text-sm">{g}</span>)}</div>
        </div>
      )}

      <div className="rounded-lg border border-border bg-card p-5 sm:p-6 space-y-2">
        <h2 className="font-head font-bold text-lg flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-primary" />Privacy</h2>
        <p className="text-sm text-muted-foreground flex items-start gap-2"><Info className="w-4 h-4 mt-0.5 shrink-0" />inizio is educational and never claims to be a doctor, lawyer or licensed adviser. We store only what's needed for your learning. You control your data.</p>
      </div>

      <button onClick={logout} data-testid="profile-logout" className="inline-flex items-center gap-2 rounded-full border border-destructive text-destructive font-bold px-5 py-2.5 hover:bg-destructive hover:text-destructive-foreground transition"><LogOut className="w-4 h-4" />Log out</button>
    </div>
  );
}
